import { MAZE_CELL_SIZE, MAZE_WALL_CUBE_INSET_PX, MAZE_WALL_THICKNESS } from '@/constants/maze';
import type { MazeStagePixiPalette } from '@/theme/maze-stage-palette';
import * as PIXI from 'pixi.js';

type WallRect = {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function clampRect(
  x: number,
  y: number,
  width: number,
  height: number,
  mazeWidth: number,
  mazeHeight: number,
): WallRect {
  const safeX = clamp(x, 0, mazeWidth);
  const safeY = clamp(y, 0, mazeHeight);
  return {
    x: safeX,
    y: safeY,
    width: clamp(width, 0, mazeWidth - safeX),
    height: clamp(height, 0, mazeHeight - safeY),
  };
}

export function drawExtrudedWallRect(
  wallGraphics: PIXI.Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
  mazeWidth: number,
  mazeHeight: number,
  palette: MazeStagePixiPalette,
): void {
  const rect = clampRect(x, y, width, height, mazeWidth, mazeHeight);
  if (rect.width <= 0 || rect.height <= 0) {
    return;
  }

  const cornerRadius = 5;
  wallGraphics.beginFill(palette.wallTop);
  wallGraphics.drawRoundedRect(rect.x, rect.y, rect.width, rect.height, cornerRadius);
  wallGraphics.endFill();

  wallGraphics.lineStyle(1, palette.wallShadow, palette.wallShadowAlpha);
  wallGraphics.drawRoundedRect(
    rect.x + 0.5,
    rect.y + 0.5,
    Math.max(0, rect.width - 1),
    Math.max(0, rect.height - 1),
    Math.max(0, cornerRadius - 0.5),
  );
  wallGraphics.lineStyle(0);

  wallGraphics.lineStyle(1, 0xffffff, 0.08);
  wallGraphics.moveTo(rect.x + 1, rect.y + 1);
  wallGraphics.lineTo(rect.x + rect.width - 2, rect.y + 1);
  wallGraphics.lineStyle(0);
}

export function drawServerGridBoundaryWalls(
  wallGraphics: PIXI.Graphics,
  grid: number[][],
  mazeCols: number,
  mazeRows: number,
  mazeWidth: number,
  mazeHeight: number,
  palette: MazeStagePixiPalette,
): void {
  const cs = MAZE_CELL_SIZE;
  const wallPx = Math.max(cs - MAZE_WALL_CUBE_INSET_PX * 2, MAZE_WALL_THICKNESS);
  const rects: WallRect[] = [];

  const cellAt = (r: number, c: number): number => {
    if (r < 0 || c < 0 || r >= mazeRows || c >= mazeCols) {
      return -1;
    }
    const v = grid[r]?.[c];
    return v === 0 || v === 1 ? v : -1;
  };

  const pushCenteredRect = (centerX: number, centerY: number, width: number, height: number) => {
    rects.push(
      clampRect(centerX - width / 2, centerY - height / 2, width, height, mazeWidth, mazeHeight),
    );
  };

  for (let r = 0; r < mazeRows; r += 1) {
    for (let c = 0; c < mazeCols; c += 1) {
      if (cellAt(r, c) !== 1) {
        continue;
      }

      const centerX = c * cs + cs / 2;
      const centerY = r * cs + cs / 2;

      pushCenteredRect(centerX, centerY, wallPx, wallPx);
    }
  }

  for (const rect of rects) {
    drawExtrudedWallRect(
      wallGraphics,
      rect.x,
      rect.y,
      rect.width,
      rect.height,
      mazeWidth,
      mazeHeight,
      palette,
    );
  }
}
